import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LabeledField } from '@/__core/components/LabeledField';
import { EmployeeDisplay, EmployeeAvatarGroup } from '@/__core/components';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import AddProgressModal from './AddProgressModal';
import { useSelector, useDispatch } from 'react-redux';
import { fetchEmployeesThunk } from '@/modules/employees/redux/EmployeeRedux';
import { RootState } from '@/setup/redux/RootReducer';
import {
  getOrderProgressItems,
  createOrderProgressItem,
  updateOrderProgressItem,
  deleteOrderProgressItem,
} from '../redux/OrderProgressCRUD';
import { getOrderById, updateOrderDates } from '@/modules/orders/redux/OrderCRUD';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';
import { useOrderProgressPermissions } from '../hooks/useOrderProgressPermissions';
import { NumericFormat } from 'react-number-format';

interface ProgressItem {
  id?: string;
  date: Date | null;
  employee_ids: string[];
  note: string;
}

type OrderStatusRef = {
  id: string;
  name?: string;
  is_record_sales?: boolean;
};

interface OrderProgressProps {
  orderId?: string;
  orderItems?: any[];
  productTypes?: any[];
  statusId?: string;
  orderStatuses?: OrderStatusRef[];
  disabled?: boolean;
  onDateChangesChange?: (hasChanges: boolean) => void;
  onSaveDatesRef?: (saveFn: (() => Promise<void>) | null) => void;
}

const resolveIsRecordSales = (
  statusFromOrder: unknown,
  statusId?: string,
  orderStatuses?: OrderStatusRef[]
): boolean => {
  const apiStatus =
    statusFromOrder && typeof statusFromOrder === 'object'
      ? (statusFromOrder as { id?: string; is_record_sales?: boolean })
      : null;

  // Prefer status object from order API (has accurate is_record_sales)
  if (apiStatus) {
    if (!statusId || !apiStatus.id || apiStatus.id === statusId) {
      if (typeof apiStatus.is_record_sales === 'boolean') {
        return apiStatus.is_record_sales;
      }
    }
  }

  if (statusId && orderStatuses?.length) {
    const matched = orderStatuses.find((status) => status.id === statusId);
    if (matched && typeof matched.is_record_sales === 'boolean') {
      return matched.is_record_sales;
    }
  }

  return false;
};

const toDisplaySalesAmount = (amount: number, isRecordSales: boolean): number =>
  isRecordSales ? amount : 0;

const formatDate = (date: Date | null | string | undefined): string => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
};

const getEmployeeNames = (employeeIds: string[], employees: Array<{ id: string; name: string; nickname?: string }>): string => {
  if (!employeeIds || employeeIds.length === 0) return '';
  const names = employeeIds
    .map(id => {
      const employee = employees.find(emp => emp.id === id);
      return employee?.nickname || employee?.name || '';
    })
    .filter(name => name !== '');
  return names.join(', ');
};

const getProgressEmployeeIds = (item: any): string[] => {
  if (Array.isArray(item.employee_ids) && item.employee_ids.length > 0) {
    return item.employee_ids;
  }

  if (Array.isArray(item.employees)) {
    return item.employees
      .map((employee: any) => employee?.id)
      .filter((id: unknown): id is string => typeof id === 'string' && id.trim() !== '');
  }

  return [];
};

// Helper function to sort progress items by date descending (newest first)
const sortProgressItemsByDate = (items: ProgressItem[]): ProgressItem[] => {
  return [...items].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.getTime() - a.date.getTime();
  });
};

const OrderProgress: React.FC<OrderProgressProps> = ({
  orderId,
  orderItems: orderItemsProp = [],
  productTypes: productTypesProp = [],
  statusId,
  orderStatuses = [],
  disabled = false,
  onDateChangesChange,
  onSaveDatesRef,
}) => {
  const dispatch = useDispatch();
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();
  const { canCreate, canUpdate, canDelete } = useOrderProgressPermissions();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProgressItem | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Separate state for completion_date and reward_date - loaded from order
  // We use temporary states that only update when user clicks "Cập nhật"
  const [tempCompletionDate, setTempCompletionDate] = useState<Date | null>(null);
  const [tempRewardDate, setTempRewardDate] = useState<Date | null>(null);
  const [originalCompletionDate, setOriginalCompletionDate] = useState<Date | null>(null);
  const [originalRewardDate, setOriginalRewardDate] = useState<Date | null>(null);
  const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);
  const [employeeMetaMap, setEmployeeMetaMap] = useState<Map<string, { name: string; nickname?: string | null; avatar?: string | null }>>(new Map());
  const [loadingOrderData, setLoadingOrderData] = useState(false);
  const [savingDates, setSavingDates] = useState(false);
  const [orderStatusFromApi, setOrderStatusFromApi] = useState<unknown>(null);

  const isRecordSales = useMemo(
    () => resolveIsRecordSales(orderStatusFromApi, statusId, orderStatuses),
    [orderStatusFromApi, statusId, orderStatuses]
  );

  const salesRows = useMemo(() => {
    return productTypesProp
      .filter((pt) => orderItemsProp.some((item) => item.item_type_tax === pt.id))
      .map((pt) => {
        const rawTotal = orderItemsProp
          .filter((item) => item.item_type_tax === pt.id)
          .reduce((sum, item) => sum + (parseFloat(item.total_price_not_tax) || 0), 0);
        return {
          id: pt.id,
          name: pt.name,
          displayTotal: toDisplaySalesAmount(rawTotal, isRecordSales),
        };
      });
  }, [productTypesProp, orderItemsProp, isRecordSales]);

  const salesGrandTotal = useMemo(() => {
    const rawTotal = orderItemsProp.reduce(
      (sum, item) => sum + (parseFloat(item.total_price_not_tax) || 0),
      0
    );
    return toDisplaySalesAmount(rawTotal, isRecordSales);
  }, [orderItemsProp, isRecordSales]);

  // Get employees from Redux store instead of local state
  const reduxEmployees = useSelector((state: RootState) => state.employees?.employees || []);
  const employeesLoading = useSelector((state: RootState) => state.employees?.loading || false);

  // Fetch employees from backend only if not already in Redux store
  useEffect(() => {
    if (reduxEmployees.length === 0 && !employeesLoading) {
      dispatch(fetchEmployeesThunk() as any);
    }
  }, [dispatch, reduxEmployees.length, employeesLoading]);

  // Load order data (completion_date, reward_date) and progress items when orderId is available
  useEffect(() => {
    if (orderId) {
      const loadOrderProgressData = async () => {
        setLoadingOrderData(true);
        try {
          // Load order to get completion_date and reward_date
          const orderResponse = await getOrderById(orderId);
          if (orderResponse && orderResponse.data) {
            const order = orderResponse.data;
            const completionDate = order.completion_date ? new Date(order.completion_date) : null;
            const rewardDate = order.reward_date ? new Date(order.reward_date) : null;
            setTempCompletionDate(completionDate);
            setTempRewardDate(rewardDate);
            setOriginalCompletionDate(completionDate);
            setOriginalRewardDate(rewardDate);
            setOrderStatusFromApi(order.status ?? null);
          }

          // Load progress items
          const items = await getOrderProgressItems(orderId);
          const transformedItems: ProgressItem[] = items.map((item: any) => ({
            id: item.id,
            date: item.date ? new Date(item.date) : null,
            employee_ids: getProgressEmployeeIds(item),
            note: item.note || '',
          }));

          // Build employee metadata from API response
          const metaMap = new Map<string, { name: string; nickname?: string | null; avatar?: string | null }>();
          items.forEach((item: any) => {
            const employees = Array.isArray(item.employees) ? item.employees : [];
            employees.forEach((emp: any) => {
              if (emp.id && !metaMap.has(emp.id)) {
                metaMap.set(emp.id, {
                  name: emp.name || '',
                  nickname: emp.nickname || null,
                  avatar: emp.user?.avatar || null,
                });
              }
            });
          });
          setEmployeeMetaMap(metaMap);
          
          // Sort by date descending (newest first)
          setProgressItems(sortProgressItemsByDate(transformedItems));
        } catch (error) {
          console.error('Error loading order progress data:', error);
          setSnackbar({
            open: true,
            message: 'Không thể tải dữ liệu tiến độ',
            severity: 'error',
          });
        } finally {
          setLoadingOrderData(false);
        }
      };
      
      loadOrderProgressData();
    } else {
      // Reset state if no orderId
      setTempCompletionDate(null);
      setTempRewardDate(null);
      setOriginalCompletionDate(null);
      setOriginalRewardDate(null);
      setProgressItems([]);
      setOrderStatusFromApi(null);
    }
  }, [orderId]); // Only load when orderId changes

  // Convert Redux employees to the format needed by this component
  const employees = useMemo(() => {
    return reduxEmployees.map((emp: any) => ({
      id: emp.id,
      name: emp.name,
      nickname: emp.nickname || undefined,
      avatar: emp.user?.avatar || null,
      company: emp.company ? {
        id: emp.company.id,
        name: emp.company.name,
        code: emp.company.code,
      } : undefined,
    }));
  }, [reduxEmployees]);

  // Helper function to compare dates (ignoring time)
  const compareDates = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 && !date2) return true;
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Check if dates have changed
  const hasDateChanges = useMemo(() => {
    return (
      !compareDates(tempCompletionDate, originalCompletionDate) ||
      !compareDates(tempRewardDate, originalRewardDate)
    );
  }, [tempCompletionDate, tempRewardDate, originalCompletionDate, originalRewardDate]);

  // Notify parent when date changes status changes
  useEffect(() => {
    if (onDateChangesChange) {
      onDateChangesChange(hasDateChanges);
    }
  }, [hasDateChanges, onDateChangesChange]);

  // Handle completion date change - only update local state, don't save yet
  const handleCompletionDateChange = (date: Date | null) => {
    setTempCompletionDate(date);
  };

  // Handle reward date change - only update local state, don't save yet
  const handleRewardDateChange = (date: Date | null) => {
    setTempRewardDate(date);
  };


  // Save both dates to backend
  const handleSaveDates = useCallback(async () => {
    if (!canUpdate) {
      setSnackbar({
        open: true,
        message: 'Bạn không có quyền cập nhật tiến độ đơn hàng',
        severity: 'error',
      });
      return;
    }

    if (!orderId || !hasDateChanges) return;

    setSavingDates(true);
    const loadingKey = 'update-dates';
    startGlobalLoading('Đang cập nhật...', loadingKey);

    try {
      await updateOrderDates(orderId, {
        completion_date: tempCompletionDate ? tempCompletionDate.toISOString().split('T')[0] : null,
        reward_date: tempRewardDate ? tempRewardDate.toISOString().split('T')[0] : null,
      });
      
      // Update original values after successful save
      setOriginalCompletionDate(tempCompletionDate);
      setOriginalRewardDate(tempRewardDate);
      
      setSnackbar({
        open: true,
        message: 'Cập nhật ngày thành công',
        severity: 'success',
      });
    } catch (error: any) {
      // Revert to original values on error
      setTempCompletionDate(originalCompletionDate);
      setTempRewardDate(originalRewardDate);
      
      setSnackbar({
        open: true,
        message: formatErrorForSnackbar(error) || 'Có lỗi xảy ra khi cập nhật',
        severity: 'error',
      });
      throw error; // Re-throw to let parent handle
    } finally {
      setSavingDates(false);
      stopGlobalLoading(loadingKey);
    }
  }, [
    canUpdate,
    orderId,
    hasDateChanges,
    tempCompletionDate,
    tempRewardDate,
    originalCompletionDate,
    originalRewardDate,
    startGlobalLoading,
    stopGlobalLoading,
  ]);

  // Expose save function to parent via ref
  useEffect(() => {
    if (onSaveDatesRef) {
      onSaveDatesRef(handleSaveDates);
      
      // Cleanup: set ref to null when component unmounts or orderId changes
      return () => {
        onSaveDatesRef(null);
      };
    }
  }, [onSaveDatesRef, handleSaveDates, orderId]);

  const handleAddItem = async (item: ProgressItem) => {
    if (editingItem?.id && !canUpdate) {
      setSnackbar({
        open: true,
        message: 'Bạn không có quyền cập nhật tiến độ đơn hàng',
        severity: 'error',
      });
      return;
    }

    if (!editingItem?.id && !canCreate) {
      setSnackbar({
        open: true,
        message: 'Bạn không có quyền tạo tiến độ đơn hàng',
        severity: 'error',
      });
      return;
    }

    if (!orderId) {
      // If no orderId (creating new order), just add to local state and sort by date descending
      const newItem = {
        ...item,
        id: `progress-${Date.now()}-${Math.random()}`,
      };
      setProgressItems(prev => sortProgressItemsByDate([...prev, newItem]));
      setModalOpen(false);
      setEditingItem(null);
      return;
    }

    const loadingKey = editingItem?.id ? `update-progress-${editingItem.id}` : 'create-progress';
    startGlobalLoading(editingItem?.id ? 'Đang cập nhật...' : 'Đang tạo...', loadingKey);

    try {
      if (editingItem?.id) {
        // Update existing item
        const dateStr = item.date ? item.date.toISOString().split('T')[0] : '';
        await updateOrderProgressItem(editingItem.id, {
          date: dateStr,
          employee_ids: item.employee_ids,
          note: item.note,
        });
        
        // Update local state and sort by date descending
        setProgressItems(prev => {
          const updated = prev.map(i => 
            i.id === editingItem.id ? { ...item, id: editingItem.id } : i
          );
          return sortProgressItemsByDate(updated);
        });
        
        setSnackbar({
          open: true,
          message: 'Cập nhật tiến độ thành công',
          severity: 'success',
        });
      } else {
        // Create new item
        const dateStr = item.date ? item.date.toISOString().split('T')[0] : '';
        const newProgressItem = await createOrderProgressItem({
          order_id: orderId,
          date: dateStr,
          employee_ids: item.employee_ids,
          note: item.note || '',
        });
        
        // Add to local state and sort by date descending
        setProgressItems(prev => sortProgressItemsByDate([...prev, {
          id: newProgressItem.id,
          date: item.date,
          employee_ids: item.employee_ids,
          note: item.note,
        }]));
        
        setSnackbar({
          open: true,
          message: 'Thêm tiến độ thành công',
          severity: 'success',
        });
      }
      
      setModalOpen(false);
      setEditingItem(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: formatErrorForSnackbar(error) || 'Có lỗi xảy ra',
        severity: 'error',
      });
    } finally {
      stopGlobalLoading(loadingKey);
    }
  };

  const handleEditItem = (item: ProgressItem) => {
    if (!canUpdate) {
      setSnackbar({
        open: true,
        message: 'Bạn không có quyền cập nhật tiến độ đơn hàng',
        severity: 'error',
      });
      return;
    }

    setEditingItem(item);
    setModalOpen(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!canDelete) {
      setSnackbar({
        open: true,
        message: 'Bạn không có quyền xóa tiến độ đơn hàng',
        severity: 'error',
      });
      return;
    }

    if (!orderId || !itemId || itemId.startsWith('progress-')) {
      // If no orderId or temporary ID, just remove from local state
      setProgressItems(prev => prev.filter(item => item.id !== itemId));
      return;
    }

    const loadingKey = `delete-progress-${itemId}`;
    startGlobalLoading('Đang xóa...', loadingKey);

    try {
      await deleteOrderProgressItem(itemId);
      
      // Remove from local state
      setProgressItems(prev => prev.filter(item => item.id !== itemId));
      
      setSnackbar({
        open: true,
        message: 'Xóa tiến độ thành công',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: formatErrorForSnackbar(error) || 'Có lỗi xảy ra khi xóa',
        severity: 'error',
      });
    } finally {
      stopGlobalLoading(loadingKey);
    }
  };

  const handleOpenModal = () => {
    if (!canCreate) {
      setSnackbar({
        open: true,
        message: 'Bạn không có quyền tạo tiến độ đơn hàng',
        severity: 'error',
      });
      return;
    }

    setEditingItem(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  return (
    <Box>
      <Paper
        sx={{
          padding: "24px",
          borderRadius: 1,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          backgroundColor: "white",
        }}
      >
         <Typography
          sx={{
            ...sharedFormStyles.sectionTitle,
            marginBottom: "20px",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "12px",
          }}
        >
          Doanh số tính thưởng
        </Typography>

        {orderItemsProp.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            <Table size="small" sx={{ '& td, & th': { borderBottom: '1px solid #e5e7eb', py: 0.75, px: 1.5 } }}>
              <TableBody>
                {salesRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ fontWeight: 500, color: '#374151', width: '200px' }}>
                      {row.name}
                    </TableCell>
                    <TableCell align="right" sx={{ color: isRecordSales ? '#111827' : '#9ca3af' }}>
                      <NumericFormat
                        value={row.displayTotal}
                        displayType="text"
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={0}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#111827', borderTop: '2px solid #d1d5db' }}>
                    Tổng cộng
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: isRecordSales ? '#111827' : '#9ca3af',
                      borderTop: '2px solid #d1d5db',
                    }}
                  >
                    <NumericFormat
                      value={salesGrandTotal}
                      displayType="text"
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={0}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography sx={{ mb: 3, fontSize: '0.875rem', color: '#6b7280' }}>
            Chưa có dòng hàng để tính doanh số thưởng.
          </Typography>
        )}

        <Typography
          sx={{
            ...sharedFormStyles.sectionTitle,
            marginBottom: "20px",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "12px",
          }}
        >
          Tiến độ đơn hàng
        </Typography>

        <Grid container rowSpacing={0} columnSpacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label="Ngày hoàn thiện" labelWidth={120}>
              <DatePicker
                value={tempCompletionDate}
                onChange={(date) => handleCompletionDateChange(date)}
                format="dd/MM/yyyy"
                disabled={disabled || loadingOrderData || !canUpdate}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "Chọn ngày hoàn thiện",
                    disabled: disabled || loadingOrderData || !canUpdate,
                  },
                }}
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label="Ngày tính thưởng" labelWidth={120}>
              <DatePicker
                value={tempRewardDate}
                onChange={(date) => handleRewardDateChange(date)}
                format="dd/MM/yyyy"
                disabled={disabled || loadingOrderData || !canUpdate}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "Chọn ngày tính thưởng",
                    disabled: disabled || loadingOrderData || !canUpdate,
                  },
                }}
              />
            </LabeledField>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            disabled={disabled || loadingOrderData || !orderId || !canCreate}
            sx={sharedFormStyles.secondaryButton}
          >
            Thêm dòng
          </Button>
        </Box>

        {progressItems.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }}>
                      Ngày
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }}>
                      Nhân viên
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb' }}>
                      Ghi chú
                    </TableCell>
                    {!disabled && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', width: '100px' }}>
                        Thao tác
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {progressItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{formatDate(item.date)}</TableCell>
                      <TableCell>
                        {(() => {
                          const matched = (item.employee_ids || [])
                            .map(id => {
                              const meta = employeeMetaMap.get(id);
                              if (meta) return { id, name: meta.name, nickname: meta.nickname, avatar: meta.avatar };
                              const reduxEmp = employees.find(emp => emp.id === id);
                              if (reduxEmp) return { id, name: reduxEmp.name, nickname: reduxEmp.nickname, avatar: reduxEmp.avatar };
                              return null;
                            })
                            .filter((emp): emp is NonNullable<typeof emp> => Boolean(emp));
                          if (matched.length === 0) {
                            return (
                              <Typography sx={{ fontSize: '0.875rem', color: '#4b5563' }}>
                                -
                              </Typography>
                            );
                          }
                          if (matched.length >= 3) {
                            return (
                              <EmployeeAvatarGroup
                                employees={matched.map((emp) => ({
                                  id: emp.id,
                                  name: emp.name,
                                  nickname: emp.nickname,
                                  avatar: emp.avatar,
                                }))}
                              />
                            );
                          }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                              {matched.map((emp) => (
                                <EmployeeDisplay
                                  key={emp.id}
                                  name={emp.name}
                                  nickname={emp.nickname}
                                  avatar={emp.avatar}
                                  image1={undefined}
                                  employeeId={emp.id}
                                />
                              ))}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#4b5563',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {item.note || '-'}
                        </Typography>
                      </TableCell>
                      {!disabled && (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditItem(item)}
                              disabled={!orderId || !canUpdate}
                              sx={{
                                color: '#3b82f6',
                                '&:hover': {
                                  backgroundColor: '#eff6ff',
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => item.id && handleDeleteItem(item.id)}
                              disabled={!orderId || !canDelete}
                              sx={{
                                color: '#ef4444',
                                '&:hover': {
                                  backgroundColor: '#fef2f2',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <AddProgressModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleAddItem}
        disabled={disabled || loadingOrderData || (editingItem?.id ? !canUpdate : !canCreate)}
        initialData={editingItem}
        orderId={orderId}
      />

      <MessageSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default OrderProgress;
